import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertTriangle, Download, Layers, RefreshCw } from "lucide-react";
import { DocumentHeader } from "@/components/BasProcess/DocumentHeader";
import {
  collectRiskConsolidation,
  isIncluded,
  resolvedRiskLevel,
  type ConsolidationResult,
  type Resolutions,
  type RiskGroup,
} from "@/lib/basProcess/consolidation";
import { generateConsolidatedRiskWord } from "@/lib/documentGenerator/consolidatedWordGenerator";
import { DisciplineOverview } from "@/components/BasProcess/DisciplineOverview";

interface Props {
  projectId: string;
}

type Phase = "utforande" | "bruksskede";

const PHASE_LABEL: Record<Phase, string> = {
  utforande: "Utförande (byggskedet)",
  bruksskede: "Bruksskede",
};

export const ConsolidatedRiskSummary: React.FC<Props> = ({ projectId }) => {
  const [phase, setPhase] = useState<Phase>("utforande");
  const [result, setResult] = useState<ConsolidationResult<RiskGroup> | null>(null);
  const [resolutions, setResolutions] = useState<Resolutions>({});
  const [recordId, setRecordId] = useState<string | null>(null);
  const [title, setTitle] = useState("Samordnad risksammanställning");
  const [notes, setNotes] = useState("");
  const [projectName, setProjectName] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [onlyConflicts, setOnlyConflicts] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [data, { data: rec }, { data: proj }] = await Promise.all([
      collectRiskConsolidation(projectId, phase),
      supabase
        .from("consolidated_documents")
        .select("*")
        .eq("project_id", projectId)
        .eq("doc_type", "risk")
        .eq("phase_key", phase)
        .maybeSingle(),
      supabase.from("projects").select("name").eq("id", projectId).maybeSingle(),
    ]);
    setResult(data);
    setProjectName((proj as { name?: string } | null)?.name ?? "");
    const row = rec as unknown as Record<string, any> | null;
    setRecordId(row?.id ?? null);
    setResolutions((row?.resolutions as Resolutions) ?? {});
    setTitle(row?.title || `Samordnad risksammanställning – ${PHASE_LABEL[phase]}`);
    setNotes(row?.notes ?? "");
    setLoading(false);
  }, [projectId, phase]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    setBusy(true);
    const payload = {
      project_id: projectId,
      doc_type: "risk",
      phase_key: phase,
      title,
      notes,
      resolutions: resolutions as unknown as Record<string, unknown>,
      sources: (result?.sources ?? []) as unknown as Record<string, unknown>[],
      updated_by: (await supabase.auth.getUser()).data.user?.id ?? null,
    };
    const { data, error } = recordId
      ? await supabase.from("consolidated_documents").update(payload as any).eq("id", recordId).select("id").single()
      : await supabase.from("consolidated_documents").insert(payload as any).select("id").single();
    setBusy(false);
    if (error) return toast.error("Kunde inte spara samordningen");
    setRecordId((data as { id: string }).id);
    toast.success("Samordningen sparad");
  };

  const patch = (key: string, value: Partial<Resolutions[string]>) =>
    setResolutions((prev) => ({ ...prev, [key]: { ...prev[key], ...value } }));

  const visibleGroups = useMemo(() => {
    const groups = result?.groups ?? [];
    return onlyConflicts ? groups.filter((g) => g.conflict) : groups;
  }, [result, onlyConflicts]);

  const includedCount = (result?.groups ?? []).filter((g) => isIncluded(resolutions, g.key)).length;
  const openConflicts = (result?.groups ?? []).filter(
    (g) => g.conflict && isIncluded(resolutions, g.key) && !resolutions[g.key]?.decision
  ).length;

  const sourceLabel = useMemo(
    () => new Map((result?.sources ?? []).map((s) => [s.id, s.label])),
    [result]
  );

  const exportWord = async () => {
    if (!result) return;
    setBusy(true);
    try {
      await generateConsolidatedRiskWord({
        projectName,
        title,
        sources: result.sources,
        groups: result.groups,
        resolutions,
        notes,
        phaseLabel: PHASE_LABEL[phase],
      });
    } catch {
      toast.error("Kunde inte skapa Word-dokumentet");
    }
    setBusy(false);
  };

  return (
    <div className="space-y-4">
      <DocumentHeader
        docNumber="30c"
        title="Samordnad risksammanställning"
        description="Slå ihop flera projektörers risksammanställningar och inlämningar till ett gemensamt dokument. Lika risker grupperas och skillnader lyfts fram för beslut."
        legalRef="AFS 2023:3 kap. 4"
        icon={Layers}
        projectId={projectId}
        meta={
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="secondary">{result?.sources.length ?? 0} källor</Badge>
            <Badge variant="secondary">{includedCount} risker</Badge>
            {openConflicts > 0 && (
              <Badge variant="destructive" className="gap-1">
                <AlertTriangle className="h-3 w-3" /> {openConflicts} obeslutade skillnader
              </Badge>
            )}
          </div>
        }
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => void load()} disabled={busy}>
              <RefreshCw className="mr-1 h-4 w-4" /> Uppdatera underlag
            </Button>
            <Button variant="outline" size="sm" onClick={save} disabled={busy}>
              Spara
            </Button>
            <Button size="sm" onClick={exportWord} disabled={busy || !result}>
              <Download className="mr-1 h-4 w-4" /> Word
            </Button>
          </div>
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <Tabs value={phase} onValueChange={(v) => setPhase(v as Phase)}>
          <TabsList>
            <TabsTrigger value="utforande">Utförande</TabsTrigger>
            <TabsTrigger value="bruksskede">Bruksskede</TabsTrigger>
          </TabsList>
        </Tabs>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={onlyConflicts}
            onCheckedChange={(v) => setOnlyConflicts(v === true)}
          />
          Visa bara rader där källorna skiljer sig
        </label>
      </div>

      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label>Dokumentets namn</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>Kommentar från Bas-P</Label>
              <Textarea
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="T.ex. hur samordningen gått till och vad som återstår."
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {!loading && (result?.groups?.length ?? 0) > 0 && (
        <DisciplineOverview groups={result!.groups} />
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground">Läser underlag …</p>
      ) : visibleGroups.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground">
            Inget underlag att samordna ännu. Skapa risksammanställningar eller be projektörerna
            lämna in sina kvarvarande risker (Dok 16).
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {visibleGroups.map((g) => {
            const r = resolutions[g.key] ?? {};
            return (
              <Card key={g.key} className={g.conflict ? "border-destructive/40" : undefined}>
                <CardContent className="space-y-3 p-4">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <p className="font-medium">{g.question}</p>
                      <p className="text-xs text-muted-foreground">{g.category}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {g.conflict && (
                        <Badge variant="destructive" className="gap-1">
                          <AlertTriangle className="h-3 w-3" /> Olika bedömning
                        </Badge>
                      )}
                      <Badge variant="outline">{resolvedRiskLevel(g, r) || "Nivå saknas"}</Badge>
                      <label className="flex items-center gap-2 text-xs">
                        <Checkbox
                          checked={r.include !== false}
                          onCheckedChange={(v) => patch(g.key, { include: v === true })}
                        />
                        Ta med
                      </label>
                    </div>
                  </div>

                  <div className="space-y-2">
                    {g.entries.map((e, i) => (
                      <div key={i} className="rounded-lg border border-border/60 bg-muted/30 p-2 text-sm">
                        <p className="text-xs font-medium text-muted-foreground">
                          {sourceLabel.get(e.sourceId) ?? "Okänd källa"}
                          {e.answer ? ` · Svar: ${e.answer}` : ""}
                        </p>
                        {e.risk && <p className="whitespace-pre-wrap">{e.risk}</p>}
                        {e.action && (
                          <p className="whitespace-pre-wrap text-muted-foreground">
                            Åtgärd: {e.action}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="grid gap-2 sm:grid-cols-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Gemensam risknivå</Label>
                      <Select
                        value={r.level ?? "auto"}
                        onValueChange={(v) => patch(g.key, { level: v === "auto" ? undefined : v })}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="auto">Högsta från källorna</SelectItem>
                          <SelectItem value="low">Låg</SelectItem>
                          <SelectItem value="medium">Medel</SelectItem>
                          <SelectItem value="high">Hög</SelectItem>
                          <SelectItem value="critical">Kritisk</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1 sm:col-span-2">
                      <Label className="text-xs">Bas-P beslut och motivering</Label>
                      <Input
                        value={r.decision ?? ""}
                        onChange={(e) => patch(g.key, { decision: e.target.value })}
                        placeholder="T.ex. Vi följer konstruktörens bedömning."
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ConsolidatedRiskSummary;
